import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { useAnchor } from "hooks";
import { getImageFormat } from "lib/utils";
import { Features10Content } from "types/graphql/generated";

interface Props {
  content: Features10Content;
}

const Features10: VFC<Props> = withErrorBoundary(
  ({ content: { features10Title, features10Description, features10Card } }) => {
    useAnchor("features10");

    return (
      <StyledFeatures10 className="features10 Content">
        <h2 className="Features10__title Font_48_54_600 xl:Font_40_48_600">{features10Title}</h2>
        {features10Description && (
          <Markdown className="Features10__description Color_secondary Font_24_32_450">
            {features10Description}
          </Markdown>
        )}

        <ul className="Features10__cards">
          {features10Card.map(({ features10CardTitle, features10CardDescription, features10CardImage }) => {
            const image = features10CardImage?.data?.features10CardImage;

            return (
              <li key={features10CardTitle} className="Features10__card">
                {image ? (
                  <LegacyImage
                    {...image}
                    layout="fill"
                    objectFit="contain"
                    transform={{ format: getImageFormat(image.ext) }}
                    className="Features10__cardIcon"
                  />
                ) : null}
                <h3 className="Features10__cardTitle Font_24_32_600 sm:Font_18_26_700">{features10CardTitle}</h3>
                <Markdown className="Features10__cardDescription Color_secondary Font_18_26_450">
                  {features10CardDescription}
                </Markdown>
              </li>
            );
          })}
        </ul>
      </StyledFeatures10>
    );
  },
  {}
);

const StyledFeatures10 = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Features10__cards {
    display: flex;
    justify-content: space-between;
    margin-top: 56px;
  }

  .Features10__cards > * {
    flex: 1;
  }

  .Features10__card {
    padding-right: 24px;
  }

  .Features10__card:not(:first-child) {
    margin-left: 32px;
  }

  .Features10__cardIcon {
    position: relative;
    width: 64px;
    height: 64px;
  }

  .Features10__cardTitle {
    margin-top: 32px;
  }

  .Features10__description {
    margin-top: 16px;
    max-width: 850px;
  }

  .Features10__cardDescription {
    margin-top: 16px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features10__cards {
      margin-top: 40px;
    }

    .Features10__card {
      padding-right: 0;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Features10__title {
      max-width: 428px;
    }

    /*
    .Features10__title {
      max-width: 100%;
    } */

    .Features10__cards {
      flex-direction: column;
      margin-top: 48px;
    }

    .Features10__cardTitle {
      margin-top: 16px;
    }

    .Features10__cardDescription {
      margin-top: 8px;
    }

    .Features10__card:not(:first-child) {
      margin-left: 0;
      margin-top: 40px;
    }
  }
`;

gql`
  fragment Features10Content on ComponentFeaturesFeatures10 {
    features10Title
    features10Description
    features10Card {
      features10CardTitle
      features10CardDescription
      features10CardImage {
        data {
          features10CardImage: attributes {
            src: hash
            alt: alternativeText
            ext
          }
        }
      }
    }
  }
`;

export { Features10 };
export type { Props as Features10Props };
